import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Cloud, CloudOff, Loader2, Check, AlertCircle, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { onCloudSync } from '@/lib/cloudSyncEvents';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type HealthStatus = 'unknown' | 'ok' | 'error';

interface LastSaveInfo {
  ibkr: string | null;
  revolut: string | null;
  sma: string | null;
  positions: string | null;
  searchResults: string | null;
}

export function AutoSaveIndicator() {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [health, setHealth] = useState<HealthStatus>('unknown');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [lastSaveInfo, setLastSaveInfo] = useState<LastSaveInfo>({
    ibkr: null,
    revolut: null,
    sma: null,
    positions: null,
    searchResults: null,
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const idleResetTimer = useRef<number | null>(null);

  // Check authentication
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session?.user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAndSyncData = useCallback(async () => {
    // This is a *health check* + last-saved aggregator.
    // It should not be blocked by authentication (we want a constant red/green state).
    setStatus((prev) => (prev === 'saving' ? 'saving' : 'idle'));
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setHealth('error');
        return;
      }

      setHealth('ok');

      const userId = session.user.id;
      let latestSave: Date | null = null;
      const saveInfo: LastSaveInfo = {
        ibkr: null,
        revolut: null,
        sma: null,
        positions: null,
        searchResults: null,
      };

      // Check IBKR imports
      const { data: ibkrData } = await supabase
        .from('ibkr_imports')
        .select('updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (ibkrData?.updated_at) {
        const date = new Date(ibkrData.updated_at);
        saveInfo.ibkr = ibkrData.updated_at;
        if (!latestSave || date > latestSave) latestSave = date;
      }

      // Check Revolut imports
      const { data: revolutData } = await supabase
        .from('revolut_imports')
        .select('updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (revolutData?.updated_at) {
        const date = new Date(revolutData.updated_at);
        saveInfo.revolut = revolutData.updated_at;
        if (!latestSave || date > latestSave) latestSave = date;
      }

      // Check SMA imports
      const { data: smaData } = await supabase
        .from('sma_surveillance_imports')
        .select('updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (smaData?.updated_at) {
        const date = new Date(smaData.updated_at);
        saveInfo.sma = smaData.updated_at;
        if (!latestSave || date > latestSave) latestSave = date;
      }

      // Check Positions
      const { data: positionsData } = await supabase
        .from('positions')
        .select('updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (positionsData?.updated_at) {
        const date = new Date(positionsData.updated_at);
        saveInfo.positions = positionsData.updated_at;
        if (!latestSave || date > latestSave) latestSave = date;
      }

      // Check Search Results
      const { data: searchData } = await (supabase as any)
        .from('search_results')
        .select('updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (searchData?.updated_at) {
        const date = new Date(searchData.updated_at);
        saveInfo.searchResults = searchData.updated_at;
        if (!latestSave || date > latestSave) latestSave = date;
      }

      setLastSaveInfo(saveInfo);
      setLastSaved(latestSave);
      // Keep whatever explicit save status came from real save events.
      // If nothing is happening, briefly show "saved" after a successful poll.
      setStatus((prev) => (prev === 'saving' ? 'saving' : 'saved'));

      if (idleResetTimer.current) window.clearTimeout(idleResetTimer.current);
      idleResetTimer.current = window.setTimeout(() => setStatus('idle'), 3000);

    } catch (error) {
      console.error('Auto-save check error:', error);
      setStatus('error');
      setHealth('error');
      if (idleResetTimer.current) window.clearTimeout(idleResetTimer.current);
      idleResetTimer.current = window.setTimeout(() => setStatus('idle'), 5000);
    }
  }, [isAuthenticated]);

  // Initial check and periodic sync every 60 seconds
  useEffect(() => {
    checkAndSyncData();
    const interval = setInterval(checkAndSyncData, 60000); // Every 60 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated, checkAndSyncData]);

  // Listen to real save events (imports / searches) to drive the UI.
  useEffect(() => {
    return onCloudSync((detail) => {
      if (detail.status === 'saving') {
        setStatus('saving');
        setHealth('ok');
        if (idleResetTimer.current) window.clearTimeout(idleResetTimer.current);
      } else if (detail.status === 'saved') {
        setStatus('saved');
        setHealth('ok');
        if (idleResetTimer.current) window.clearTimeout(idleResetTimer.current);
        idleResetTimer.current = window.setTimeout(() => setStatus('idle'), 3000);
      } else {
        setStatus('error');
        setHealth('error');
        if (idleResetTimer.current) window.clearTimeout(idleResetTimer.current);
        idleResetTimer.current = window.setTimeout(() => setStatus('idle'), 5000);
      }
    });
  }, []);

  const formatLastSaved = (date: Date | null) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDetailedTime = (timestamp: string | null) => {
    if (!timestamp) return 'No data';
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'saving':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case 'saved':
        return <Check className="h-4 w-4 text-success" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      default:
        if (!isAuthenticated) return <CloudOff className="h-4 w-4 text-destructive" />;
        return lastSaved ? <Cloud className="h-4 w-4 text-muted-foreground" /> : <Cloud className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'saving':
        return 'Syncing...';
      case 'saved':
        return 'Synced';
      case 'error':
        return 'Sync error';
      default:
        return isAuthenticated ? formatLastSaved(lastSaved) : 'Sign in';
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-default",
            "bg-muted/50 border border-border/50 backdrop-blur-sm",
            status === 'saving' && "bg-primary/10 border-primary/30",
            status === 'saved' && "bg-success/10 border-success/30",
            status === 'error' && "bg-destructive/10 border-destructive/30",
            !isAuthenticated && "bg-destructive/10 border-destructive/30"
          )}
        >
          {/* Always-on health dot (red/green) */}
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              health === 'ok' && 'bg-success',
              (health === 'error' || !isAuthenticated) && 'bg-destructive',
              health === 'unknown' && 'bg-muted-foreground'
            )}
            aria-label={health === 'ok' ? 'Cloud healthy' : 'Cloud error'}
          />

          <div className={cn(
            "relative",
            status === 'saving' && "animate-pulse"
          )}>
            {getStatusIcon()}
            {status === 'saving' && (
              <ArrowUpRight className="absolute -bottom-2 -right-2 h-3 w-3 text-primary animate-pulse" />
            )}
            {status === 'saving' && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full animate-ping" />
            )}
          </div>
          <span className={cn(
            "hidden sm:inline transition-colors",
            status === 'saving' && "text-primary",
            status === 'saved' && "text-success",
            status === 'error' && "text-destructive",
            status === 'idle' && "text-muted-foreground"
          )}>
            {getStatusText()}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <div className="space-y-2 text-xs">
          <p className="font-semibold text-foreground">Cloud Sync Status</p>
          {!isAuthenticated && (
            <p className="text-muted-foreground">Sign in to enable saving and cloud sync.</p>
          )}
          <div className="space-y-1.5 text-muted-foreground">
            <div className="flex justify-between gap-4">
              <span>IBKR Analysis:</span>
              <span className="font-medium text-foreground">{formatDetailedTime(lastSaveInfo.ibkr)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Revolut:</span>
              <span className="font-medium text-foreground">{formatDetailedTime(lastSaveInfo.revolut)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Surveillance:</span>
              <span className="font-medium text-foreground">{formatDetailedTime(lastSaveInfo.sma)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Positions:</span>
              <span className="font-medium text-foreground">{formatDetailedTime(lastSaveInfo.positions)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>AI/Web Search:</span>
              <span className="font-medium text-foreground">{formatDetailedTime(lastSaveInfo.searchResults)}</span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">
            Auto-syncs every 60 seconds
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
